import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useThemeMode } from "../../ThemeContext";
import { APP_URLS } from "../../helpers/url_helper";
import { dataAPI } from "../../helpers/api_service";

const DROPDOWN_OTHER_TEXT_SUFFIX = "__other_text";

const ATTACHMENT_MAX_BYTES = 8 * 1024 * 1024;
const ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/gif,image/webp";

const ATTACHMENT_EXT_RE = /\.(pdf|doc|docx|png|jpe?g|gif|webp)$/i;

const isAllowedAttachmentFile = (file) => {
  if (!file || typeof file.name !== "string") return false;
  if (!ATTACHMENT_EXT_RE.test(file.name.trim())) return false;
  const mime = String(file.type || "").toLowerCase();
  if (!mime) return true;
  if (mime === "application/pdf") return true;
  if (mime === "application/msword") return true;
  if (
    mime ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  )
    return true;
  if (mime.startsWith("image/")) return true;
  return false;
};

const getDefaultValue = (field) => {
  if (field?.type === "checkbox" || field?.type === "multi_select") return [];
  if (field?.type === "attachment") return null;
  return "";
};

const isDropdownOtherSelection = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase() === "other";

const isSiteDataSource = (sourceType) => ["site", "sites"].includes(sourceType);

const resolveDataSourceKey = (sourceType) => {
  if (sourceType === "project") return "projects";
  if (sourceType === "task") return "tasks";
  if (isSiteDataSource(sourceType)) return "sites";
  return sourceType;
};

const getAnswerForSubmit = (field, values) => {
  const raw = values[field?.id];
  const hasDataSource = String(field?.dataSource || "").trim().length > 0;

  if (field?.type === "multi_select") {
    if (!Array.isArray(raw)) return "";
    return raw
      .map((item) => String(item ?? "").trim())
      .filter(Boolean)
      .join(", ");
  }

  if (
    field?.type === "dropdown" &&
    !hasDataSource &&
    isDropdownOtherSelection(raw)
  ) {
    return String(values[`${field.id}${DROPDOWN_OTHER_TEXT_SUFFIX}`] ?? "").trim();
  }

  return raw;
};

function CompanyFormFill() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const template = location.state?.template || null;
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitMessageType, setSubmitMessageType] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYouView, setShowThankYouView] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState("");
  const [dataSourceOptions, setDataSourceOptions] = useState({
    employees: [],
    assets: [],
    projects: [],
    tasks: [],
    sites: [],
  });
  const fetchedSourcesRef = useRef({
    employees: false,
    assets: false,
    projects: false,
    tasks: false,
    sites: false,
  });
  const sections = useMemo(() => {
    const source = Array.isArray(template?.schema_json) ? template.schema_json : [];
    if (!source.length) return [];

    const isSectionSchema = Array.isArray(source[0]?.fields);
    if (isSectionSchema) {
      return source.map((section, sectionIdx) => ({
        id: section?.id || `section-${sectionIdx + 1}`,
        title: section?.title || `Section ${sectionIdx + 1}`,
        description: section?.description || "",
        section_order: section?.section_order || sectionIdx + 1,
        fields: Array.isArray(section?.fields) ? section.fields : [],
      }));
    }

    // Backward compatibility: old flat schema into one section
    return [
      {
        id: "section-1",
        title: "Section 1",
        description: "",
        section_order: 1,
        fields: source,
      },
    ];
  }, [template]);

  const fields = useMemo(
    () => sections.flatMap((section) => section.fields || []),
    [sections],
  );

  const [formValues, setFormValues] = useState(() => {
    const values = {};
    fields.forEach((field) => {
      values[field.id] = getDefaultValue(field);
      if (field.type === "dropdown") {
        values[`${field.id}${DROPDOWN_OTHER_TEXT_SUFFIX}`] = "";
      }
    });
    return values;
  });

  const buildInitialValues = useCallback(() => {
    const values = {};
    fields.forEach((field) => {
      values[field.id] = getDefaultValue(field);
      if (field.type === "dropdown") {
        values[`${field.id}${DROPDOWN_OTHER_TEXT_SUFFIX}`] = "";
      }
    });
    return values;
  }, [fields]);

  useEffect(() => {
    setFormValues(buildInitialValues());
  }, [sections, buildInitialValues]);

  useEffect(() => {
    const isListFieldWithSource = (field) =>
      field?.type === "dropdown" || field?.type === "multi_select";

    const hasEmployeeDataSource = fields.some(
      (field) =>
        isListFieldWithSource(field) &&
        String(field?.dataSource || "").trim().toLowerCase() === "employees",
    );
    const hasProjectDataSource = fields.some(
      (field) =>
        isListFieldWithSource(field) &&
        ["projects", "project"].includes(
          String(field?.dataSource || "").trim().toLowerCase(),
        ),
    );
    const hasAssetDataSource = fields.some(
      (field) =>
        isListFieldWithSource(field) &&
        ["assets", "asset"].includes(
          String(field?.dataSource || "").trim().toLowerCase(),
        ),
    );
    const hasTaskDataSource = fields.some(
      (field) =>
        isListFieldWithSource(field) &&
        ["tasks", "task"].includes(
          String(field?.dataSource || "").trim().toLowerCase(),
        ),
    );
    const hasSiteDropdownDataSource = fields.some(
      (field) =>
        field?.type === "dropdown" &&
        isSiteDataSource(String(field?.dataSource || "").trim().toLowerCase()),
    );

    if (
      !hasEmployeeDataSource &&
      !hasProjectDataSource &&
      !hasAssetDataSource &&
      !hasTaskDataSource &&
      !hasSiteDropdownDataSource
    )
      return;

    const loadEmployees = async () => {
      try {
        const response = await dataAPI.getAllEmployees();
        const employees = Array.isArray(response?.data) ? response.data : [];
        const employeeOptions = employees.map((employee) => ({
          value: String(employee?.id ?? ""),
          label:
            employee?.full_name ||
            employee?.username ||
            employee?.email ||
            `Employee ${employee?.id ?? ""}`,
        }));
        setDataSourceOptions((prev) => ({ ...prev, employees: employeeOptions }));
      } catch (error) {
        console.error("Error loading employees for form datasource:", error);
        setDataSourceOptions((prev) => ({ ...prev, employees: [] }));
      }
    };

    const loadProjects = async () => {
      try {
        const response = await dataAPI.getProjectsList();
        const projects = Array.isArray(response?.data) ? response.data : [];
        const projectOptions = projects.map((project) => ({
          value: String(project?.id ?? ""),
          label:
            project?.title ||
            project?.name ||
            project?.project_name ||
            `Project ${project?.id ?? ""}`,
        }));
        setDataSourceOptions((prev) => ({ ...prev, projects: projectOptions }));
      } catch (error) {
        console.error("Error loading projects for form datasource:", error);
        setDataSourceOptions((prev) => ({ ...prev, projects: [] }));
      }
    };

    const loadAssets = async () => {
      try {
        const response = await dataAPI.getAssetRecords({ showLoading: false });
        const assets = Array.isArray(response?.data) ? response.data : [];
        const assetOptions = assets.map((asset) => ({
          value: String(asset?.asset_id || asset?.id || ""),
          label: String(asset?.asset_id || asset?.id || "") || "Asset",
        }));
        setDataSourceOptions((prev) => ({ ...prev, assets: assetOptions }));
      } catch (error) {
        console.error("Error loading assets for form datasource:", error);
        setDataSourceOptions((prev) => ({ ...prev, assets: [] }));
      }
    };

    const loadTasks = async () => {
      try {
        const response = await dataAPI.getAllTasks({ limit: 50, offset: 0 });
        const tasks = Array.isArray(response?.tasks)
          ? response.tasks
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const taskOptions = tasks.map((task) => ({
          value: String(task?.id ?? ""),
          label:
            task?.title ||
            task?.task_title ||
            task?.name ||
            `Task ${task?.id ?? ""}`,
        }));
        setDataSourceOptions((prev) => ({ ...prev, tasks: taskOptions }));
      } catch (error) {
        console.error("Error loading tasks for form datasource:", error);
        setDataSourceOptions((prev) => ({ ...prev, tasks: [] }));
      }
    };

    const loadSites = async () => {
      try {
        const response = await dataAPI.getSitesList({ showLoading: false });
        if (response?.status !== "success") {
          setDataSourceOptions((prev) => ({ ...prev, sites: [] }));
          return;
        }
        const sites = Array.isArray(response?.data) ? response.data : [];
        const siteOptions = sites
          .map((site) => {
            const siteName = String(site?.site_name ?? "").trim();
            if (!siteName) return null;
            return { value: siteName, label: siteName };
          })
          .filter(Boolean);
        setDataSourceOptions((prev) => ({ ...prev, sites: siteOptions }));
      } catch (error) {
        console.error("Error loading sites for form datasource:", error);
        setDataSourceOptions((prev) => ({ ...prev, sites: [] }));
      }
    };

    if (hasEmployeeDataSource && !fetchedSourcesRef.current.employees) {
      fetchedSourcesRef.current.employees = true;
      loadEmployees();
    }
    if (hasProjectDataSource && !fetchedSourcesRef.current.projects) {
      fetchedSourcesRef.current.projects = true;
      loadProjects();
    }
    if (hasAssetDataSource && !fetchedSourcesRef.current.assets) {
      fetchedSourcesRef.current.assets = true;
      loadAssets();
    }
    if (hasTaskDataSource && !fetchedSourcesRef.current.tasks) {
      fetchedSourcesRef.current.tasks = true;
      loadTasks();
    }
    if (hasSiteDropdownDataSource && !fetchedSourcesRef.current.sites) {
      fetchedSourcesRef.current.sites = true;
      loadSites();
    }
  }, [fields]);

  const updateValue = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field.id]: value }));
  };

  const updateDropdownValue = (field, value) => {
    setFormValues((prev) => {
      const next = { ...prev, [field.id]: value };
      if (field.type === "dropdown" && !isDropdownOtherSelection(value)) {
        next[`${field.id}${DROPDOWN_OTHER_TEXT_SUFFIX}`] = "";
      }
      return next;
    });
  };

  const updateDropdownOtherText = (field, text) => {
    setFormValues((prev) => ({
      ...prev,
      [`${field.id}${DROPDOWN_OTHER_TEXT_SUFFIX}`]: text,
    }));
  };

  const updateCheckboxValue = (field, option) => {
    const current = Array.isArray(formValues[field.id]) ? formValues[field.id] : [];
    if (current.includes(option)) {
      updateValue(field, current.filter((item) => item !== option));
    } else {
      updateValue(field, [...current, option]);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const clientFormId = template?.id || template?.client_form_id;
    if (!clientFormId) {
      setSubmitMessageType("error");
      setSubmitMessage("Client form id is missing.");
      return;
    }

    for (const section of sections) {
      for (const field of section.fields || []) {
        const hasDataSource = String(field?.dataSource || "").trim().length > 0;
        if (
          field?.type === "dropdown" &&
          !hasDataSource &&
          field.required &&
          isDropdownOtherSelection(formValues[field.id])
        ) {
          const otherText = String(
            formValues[`${field.id}${DROPDOWN_OTHER_TEXT_SUFFIX}`] ?? "",
          ).trim();
          if (!otherText) {
            setSubmitMessageType("error");
            setSubmitMessage(
              `Please enter a value for "${field.label || "this question"}" (Other).`,
            );
            return;
          }
        }

        if (field?.type === "attachment" && field.required) {
          const att = formValues[field.id];
          const hasFile = Boolean(att?.file && att.file instanceof File);
          if (!hasFile) {
            setSubmitMessageType("error");
            setSubmitMessage(
              `Please upload a file for "${field.label || "this question"}".`,
            );
            return;
          }
        }

        if (field?.type === "multi_select" && field.required) {
          const sel = formValues[field.id];
          if (!Array.isArray(sel) || sel.length === 0) {
            setSubmitMessageType("error");
            setSubmitMessage(
              `Please select at least one option for "${field.label || "this question"}".`,
            );
            return;
          }
        }
      }
    }

    const attachmentUrlByFieldId = {};

    try {
      setIsSubmitting(true);
      setSubmitMessage("");

      for (const section of sections) {
        for (const field of section.fields || []) {
          if (field?.type !== "attachment") continue;
          const att = formValues[field.id];
          const file = att?.file;
          if (!file || !(file instanceof File)) continue;

          const uploadResponse = await dataAPI.uploadDigitalFormAttachment(file, {
            showLoading: false,
          });
          if (uploadResponse?.status !== "success") {
            throw new Error(
              uploadResponse?.message || "Attachment upload failed.",
            );
          }
          const uploadedUrl = String(uploadResponse?.data?.url ?? "").trim();
          if (!uploadedUrl) {
            throw new Error("Attachment upload did not return a file URL.");
          }
          attachmentUrlByFieldId[field.id] = uploadedUrl;
        }
      }

      const data = sections.map((section, sectionIndex) => ({
        title: section?.title || `Section ${sectionIndex + 1}`,
        description: section?.description || "",
        fields: (section?.fields || []).map((field, fieldIndex) => ({
          label: field?.label || `Question ${fieldIndex + 1}`,
          answer:
            field?.type === "attachment"
              ? String(attachmentUrlByFieldId[field.id] ?? "").trim()
              : getAnswerForSubmit(field, formValues),
        })),
      }));

      const assetDropdownFields = sections.flatMap((section) =>
        (section?.fields || []).filter((field) => {
          const sourceType = String(field?.dataSource || "").trim().toLowerCase();
          return field?.type === "dropdown" && ["asset", "assets"].includes(sourceType);
        }),
      );
      const hasAssetDropdownField = assetDropdownFields.length > 0;
      const selectedAssetId =
        assetDropdownFields
          .map((field) => String(formValues[field?.id] ?? "").trim())
          .find(Boolean) || "";

      const submitPayload = {
        data,
        asset_id: hasAssetDropdownField ? selectedAssetId : "",
      };

      const response = await dataAPI.submitDigitalFormClientForm(
        clientFormId,
        submitPayload,
      );
      if (response?.status !== "success") {
        throw new Error(response?.message || "Failed to submit form.");
      }
      setThankYouMessage(response?.message || "");
      setShowThankYouView(true);
    } catch (error) {
      setSubmitMessageType("error");
      setSubmitMessage(error?.message || "Failed to submit form.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearForm = () => {
    setFormValues(buildInitialValues());
    setSubmitMessage("");
    setSubmitMessageType("success");
  };

  if (!template || !sections.length) {
    return (
      <div style={{ padding: 20 }}>
        <div style={emptyStateStyle(mode)}>
          No form selected. Please choose a form from Company Forms list.
        </div>
        <button
          type="button"
          onClick={() => navigate(APP_URLS.COMPANY_FORMS)}
          style={backButtonStyle(mode)}
        >
          <ArrowBackIcon style={{ fontSize: 18 }} />
          Back to Company Forms
        </button>
      </div>
    );
  }

  if (showThankYouView) {
    const formTitle = (template?.title || "").trim() || "this form";
    const thankYouNote = String(thankYouMessage || "").trim();
    const suppressedThankYouNotes = new Set([
      "thank you! form submitted successfully.",
      "form submitted successfully.",
    ]);
    const apiNote =
      thankYouNote && !suppressedThankYouNotes.has(thankYouNote.toLowerCase())
        ? thankYouNote
        : "";

    return (
      <div style={{ padding: 20 }}>
        <div style={wrapperStyle(mode)}>
          <div style={thankYouShellStyle(mode)}>
            <div style={thankYouCardStyle(mode)}>
              <div style={thankYouIconRingStyle(mode)} aria-hidden>
                <CheckCircleIcon style={thankYouCheckIconStyle()} />
              </div>
              <h2 style={thankYouTitleStyle(mode)}>Thank you</h2>
              <p style={thankYouLeadStyle(mode)}>
                Your response for{" "}
                <span style={thankYouFormNameStyle(mode)}>{formTitle}</span> has
                been submitted successfully.
              </p>
              {apiNote ? (
                <p style={thankYouApiNoteStyle(mode)}>{apiNote}</p>
              ) : null}
              <button
                type="button"
                style={{ ...submitButtonStyle(), marginTop: 8, minWidth: 200 }}
                onClick={() => navigate(APP_URLS.COMPANY_FORMS)}
              >
                Back to Company Forms
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={wrapperStyle(mode)}>
        <div style={{ marginBottom: 16 }}>
          <button
            type="button"
            onClick={() => navigate(APP_URLS.COMPANY_FORMS)}
            style={backButtonStyle(mode)}
          >
            <ArrowBackIcon style={{ fontSize: 18 }} />
            Back to Company Forms
          </button>
          <h2 style={titleStyle(mode)}>{template?.title || "Company Form"}</h2>
          <p style={descriptionStyle(mode)}>
            {template?.description || "Please fill the form below."}
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "grid", gap: 14 }}>
          {submitMessage && (
            <div style={messageStyle(mode, submitMessageType)}>{submitMessage}</div>
          )}

          {sections.map((section, sectionIndex) => (
            <section key={section.id || sectionIndex} style={sectionWrapStyle(mode)}>
              <div style={sectionHeaderStyle()}>
                <h3 style={sectionTitleStyle(mode)}>
                  {section.title || `Section ${sectionIndex + 1}`}
                </h3>
                {section.description ? (
                  <p style={sectionDescriptionStyle(mode)}>{section.description}</p>
                ) : null}
              </div>

              <div style={{ display: "grid", gap: 12 }}>
                {(section.fields || []).map((field) => (
                  <div key={field.id} style={fieldWrapStyle(mode)}>
                    <label style={labelStyle(mode)}>
                      {field.label || "Untitled Field"}
                      {field.required ? " *" : ""}
                    </label>
                    {renderInputField(
                      field,
                      formValues,
                      mode,
                      updateValue,
                      updateCheckboxValue,
                      dataSourceOptions,
                      updateDropdownValue,
                      updateDropdownOtherText,
                      setSubmitMessage,
                      setSubmitMessageType,
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}

          <div style={{ display: "inline-flex", gap: 10 }}>
            <button
              type="button"
              style={clearButtonStyle(mode)}
              onClick={handleClearForm}
              disabled={isSubmitting}
            >
              Clear
            </button>
            <button type="submit" style={submitButtonStyle()}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function renderInputField(
  field,
  formValues,
  mode,
  updateValue,
  updateCheckboxValue,
  dataSourceOptions,
  updateDropdownValue,
  updateDropdownOtherText,
  setSubmitMessage,
  setSubmitMessageType,
) {
  const commonInputStyle = inputStyle(mode);
  const fieldValue = formValues[field.id];
  const staticOptions = Array.isArray(field.options) ? field.options : [];
  const sourceType = String(field?.dataSource || "").trim().toLowerCase();
  const hasDataSource = sourceType.length > 0;
  const sourceKey = resolveDataSourceKey(sourceType);
  const isSiteDropdown =
    field.type === "dropdown" && hasDataSource && isSiteDataSource(sourceType);
  const sourceOptions = hasDataSource
    ? dataSourceOptions[sourceKey] || []
    : [];
  const dropdownOptions = hasDataSource
    ? isSiteDropdown
      ? sourceOptions.map((item) => ({
          value: String(item.value ?? item.label ?? "").trim(),
          label: String(item.label ?? item.value ?? "").trim(),
        }))
      : sourceOptions.map((item) => ({ value: item.label, label: item.label }))
    : staticOptions.map((option) => ({ value: option, label: option }));

  if (field.type === "attachment") {
    const attachment = fieldValue && typeof fieldValue === "object" ? fieldValue : null;
    const file = attachment?.file instanceof File ? attachment.file : null;
    const fileName = file ? file.name : "";

    const onAttachmentChange = (event) => {
      const nextFile = event.target.files?.[0];
      if (!nextFile) {
        updateValue(field, null);
        return;
      }
      if (nextFile.size > ATTACHMENT_MAX_BYTES) {
        event.target.value = "";
        setSubmitMessageType("error");
        setSubmitMessage(
          `File is too large. Maximum size is ${Math.round(ATTACHMENT_MAX_BYTES / (1024 * 1024))} MB.`,
        );
        updateValue(field, null);
        return;
      }
      if (!isAllowedAttachmentFile(nextFile)) {
        event.target.value = "";
        setSubmitMessageType("error");
        setSubmitMessage(
          "Please choose a PDF, Word document (.doc, .docx), or a common image file (PNG, JPG, GIF, WebP).",
        );
        updateValue(field, null);
        return;
      }
      setSubmitMessage("");
      updateValue(field, { file: nextFile });
    };

    return (
      <div style={{ display: "grid", gap: 8 }}>
        {field.placeholder ? (
          <p
            style={{
              margin: 0,
              fontSize: 12,
              lineHeight: 1.45,
              color: mode === "dark" ? "#94a3b8" : "#64748b",
            }}
          >
            {field.placeholder}
          </p>
        ) : null}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 10,
          }}
        >
          <label style={{ cursor: "pointer", margin: 0 }}>
            <input
              key={file ? `${file.name}-${file.size}` : "attachment-empty"}
              type="file"
              accept={ATTACHMENT_ACCEPT}
              onChange={onAttachmentChange}
              style={{ display: "none" }}
            />
            <span
              style={{
                ...commonInputStyle,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "9px 14px",
                width: "auto",
                fontWeight: 600,
                cursor: "pointer",
                background: mode === "dark" ? "#1a2e2d" : "#e6f7f6",
                borderColor: mode === "dark" ? "#2e6b69" : "#9be3df",
                color: mode === "dark" ? "#8de0db" : "#0c6a65",
              }}
            >
              Choose file
            </span>
          </label>
          <span
            style={{
              fontSize: 13,
              color: mode === "dark" ? "#cbd5e1" : "#475569",
              wordBreak: "break-all",
            }}
          >
            {fileName || "No file selected"}
          </span>
          {file ? (
            <button
              type="button"
              onClick={() => updateValue(field, null)}
              style={clearButtonStyle(mode)}
            >
              Remove
            </button>
          ) : null}
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 11,
            color: mode === "dark" ? "#64748b" : "#94a3b8",
          }}
        >
          Allowed: PDF, Word (.doc, .docx), PNG, JPG, GIF, WebP — max{" "}
          {Math.round(ATTACHMENT_MAX_BYTES / (1024 * 1024))} MB.
        </p>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <textarea
        required={Boolean(field.required)}
        value={fieldValue || ""}
        onChange={(event) => updateValue(field, event.target.value)}
        style={{ ...commonInputStyle, minHeight: 90, resize: "vertical" }}
        placeholder={field.placeholder || "Enter value"}
      />
    );
  }

  if (field.type === "multi_select") {
    const selected = Array.isArray(fieldValue) ? fieldValue : [];
    const borderColor = mode === "dark" ? "#4a4a4a" : "#d0d5dd";
    const rowBorder = mode === "dark" ? "#2a2a2a" : "#e5e7eb";
    const selectedLabels = dropdownOptions
      .filter((opt) => selected.includes(opt.value))
      .map((opt) => opt.label);

    if (!dropdownOptions.length) {
      return (
        <div
          style={{
            ...commonInputStyle,
            fontSize: 13,
            color: mode === "dark" ? "#94a3b8" : "#64748b",
          }}
        >
          No options available for this question.
        </div>
      );
    }

    return (
      <details
        style={{
          borderRadius: 8,
          overflow: "hidden",
          border: `1px solid ${borderColor}`,
          maxWidth: "100%",
          background: mode === "dark" ? "#191919" : "#fff",
        }}
      >
        <summary
          style={{
            padding: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            fontSize: 13,
            fontWeight: 600,
            color: mode === "dark" ? "#e4e7ec" : "#334155",
            cursor: "pointer",
            listStyle: "none",
          }}
        >
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
            {selectedLabels.length
              ? selectedLabels.join(", ")
              : "Select one or more options"}
          </span>
          <span style={{ fontSize: 12, opacity: 0.8 }}>⌄</span>
        </summary>
        <div
          style={{
            borderTop: `1px solid ${borderColor}`,
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          {dropdownOptions.map((option, index) => {
            const val = option.value;
            const checked = selected.includes(val);
            return (
              <label
                key={`${field.id}-ms-${index}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "9px 10px",
                  borderBottom:
                    index < dropdownOptions.length - 1
                      ? `1px solid ${rowBorder}`
                      : "none",
                  cursor: "pointer",
                  fontSize: 13,
                  color: mode === "dark" ? "#e4e7ec" : "#334155",
                  margin: 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => updateCheckboxValue(field, val)}
                  style={{ accentColor: "#009391" }}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      </details>
    );
  }

  if (field.type === "dropdown") {
    const showOtherInput =
      !hasDataSource && isDropdownOtherSelection(fieldValue);
    const otherTextValue =
      formValues[`${field.id}${DROPDOWN_OTHER_TEXT_SUFFIX}`] ?? "";

    if (isSiteDropdown && !dropdownOptions.length) {
      return (
        <div
          style={{
            ...commonInputStyle,
            fontSize: 13,
            color: mode === "dark" ? "#94a3b8" : "#64748b",
          }}
        >
          Loading sites...
        </div>
      );
    }

    return (
      <div style={{ display: "grid", gap: 8 }}>
        <select
          required={Boolean(field.required) && !showOtherInput}
          value={fieldValue || ""}
          onChange={(event) => updateDropdownValue(field, event.target.value)}
          style={commonInputStyle}
        >
          <option value="">{isSiteDropdown ? "Select site" : "Select option"}</option>
          {dropdownOptions.map((option, index) => (
            <option key={`${field.id}-${index}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {showOtherInput ? (
          <input
            type="text"
            required={Boolean(field.required)}
            value={otherTextValue}
            onChange={(event) =>
              updateDropdownOtherText(field, event.target.value)
            }
            style={commonInputStyle}
            placeholder="Please specify"
          />
        ) : null}
      </div>
    );
  }

  if (field.type === "radio") {
    return (
      <div style={{ display: "grid", gap: 8 }}>
        {staticOptions.map((option, index) => (
          <label key={`${field.id}-${index}`} style={choiceLabelStyle(mode)}>
            <input
              type="radio"
              name={field.id}
              checked={fieldValue === option}
              onChange={() => updateValue(field, option)}
              required={Boolean(field.required) && !fieldValue}
            />
            {option}
          </label>
        ))}
      </div>
    );
  }

  if (field.type === "checkbox") {
    const selected = Array.isArray(fieldValue) ? fieldValue : [];
    return (
      <div style={{ display: "grid", gap: 8 }}>
        {staticOptions.map((option, index) => (
          <label key={`${field.id}-${index}`} style={choiceLabelStyle(mode)}>
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => updateCheckboxValue(field, option)}
              style={{ accentColor: "#009391" }}
            />
            {option}
          </label>
        ))}
      </div>
    );
  }

  const htmlTypeMap = {
    text: "text",
    email: "email",
    number: "number",
    date: "date",
  };

  return (
    <input
      required={Boolean(field.required)}
      type={htmlTypeMap[field.type] || "text"}
      value={fieldValue || ""}
      onChange={(event) => updateValue(field, event.target.value)}
      style={commonInputStyle}
      placeholder={field.placeholder || "Enter value"}
    />
  );
}

const wrapperStyle = (mode) => ({
  border: "none",
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
  maxWidth: 900,
});

const titleStyle = (mode) => ({
  margin: "14px 0 6px",
  color: mode === "dark" ? "#fff" : "#0f172a",
});

const descriptionStyle = (mode) => ({
  margin: 0,
  color: mode === "dark" ? "#98a2b3" : "#64748b",
  fontSize: 13,
});

const emptyStateStyle = (mode) => ({
  border: `1px dashed ${mode === "dark" ? "#475467" : "#cbd5e1"}`,
  borderRadius: 10,
  padding: 14,
  color: mode === "dark" ? "#cbd5e1" : "#475569",
  marginBottom: 12,
});

const backButtonStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  background: "transparent",
  color: mode === "dark" ? "#fff" : "#111",
  borderRadius: 8,
  padding: "7px 12px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const fieldWrapStyle = (mode) => ({
  display: "grid",
  gap: 6,
  border: "none",
  borderRadius: 0,
  padding: 0,
});

const sectionWrapStyle = (mode) => ({
  display: "grid",
  gap: 12,
  border: `1px solid ${mode === "dark" ? "#2f2f2f" : "#e2e8f0"}`,
  borderRadius: 12,
  background: mode === "dark" ? "#141414" : "#ffffff",
  padding: 14,
});

const sectionHeaderStyle = () => ({
  display: "grid",
  gap: 4,
});

const sectionTitleStyle = (mode) => ({
  margin: 0,
  color: mode === "dark" ? "#f8fafc" : "#0f172a",
  fontSize: 18,
  fontWeight: 700,
});

const sectionDescriptionStyle = (mode) => ({
  margin: 0,
  color: mode === "dark" ? "#94a3b8" : "#64748b",
  fontSize: 13,
});

const labelStyle = (mode) => ({
  color: mode === "dark" ? "#e4e7ec" : "#1e293b",
  fontSize: 13,
  fontWeight: 600,
});

const inputStyle = (mode) => ({
  width: "100%",
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "9px 10px",
  fontSize: 13,
});

const choiceLabelStyle = (mode) => ({
  display: "inline-flex",
  gap: 8,
  alignItems: "center",
  color: mode === "dark" ? "#e4e7ec" : "#334155",
  fontSize: 13,
});

const submitButtonStyle = () => ({
  border: "none",
  borderRadius: 8,
  background: "#009391",
  color: "#fff",
  fontWeight: 600,
  fontSize: 13,
  padding: "10px 16px",
  cursor: "pointer",
});

const clearButtonStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: "transparent",
  color: mode === "dark" ? "#fff" : "#111",
  fontWeight: 600,
  fontSize: 13,
  padding: "10px 16px",
  cursor: "pointer",
});

const messageStyle = (mode, type = "success") => ({
  border:
    type === "error"
      ? `1px solid ${mode === "dark" ? "#7a3d3d" : "#f1b2b2"}`
      : `1px solid ${mode === "dark" ? "#2e6b69" : "#9be3df"}`,
  background:
    type === "error"
      ? mode === "dark"
        ? "#2b1717"
        : "#fff3f3"
      : mode === "dark"
        ? "#122c2b"
        : "#ecfbfa",
  color:
    type === "error"
      ? mode === "dark"
        ? "#ffb0b0"
        : "#a53030"
      : mode === "dark"
        ? "#8de0db"
        : "#0c6a65",
  borderRadius: 8,
  padding: "9px 12px",
  fontSize: 13,
});

const thankYouShellStyle = (mode) => ({
  display: "flex",
  justifyContent: "center",
  padding: "24px 0 8px",
});

const thankYouCardStyle = (mode) => ({
  width: "100%",
  maxWidth: 440,
  border: `1px solid ${mode === "dark" ? "#2a4a48" : "#b8e8e4"}`,
  background: mode === "dark" ? "#152525" : "#f6fdfc",
  boxShadow:
    mode === "dark"
      ? "0 12px 40px rgba(0, 0, 0, 0.45)"
      : "0 10px 40px rgba(12, 106, 101, 0.08)",
  borderRadius: 16,
  padding: "36px 28px 32px",
  display: "grid",
  gap: 14,
  justifyItems: "center",
  textAlign: "center",
});

const thankYouIconRingStyle = (mode) => ({
  width: 88,
  height: 88,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background:
    mode === "dark" ? "rgba(0, 147, 145, 0.18)" : "rgba(0, 147, 145, 0.12)",
  border: `1px solid ${mode === "dark" ? "rgba(0, 147, 145, 0.35)" : "rgba(0, 147, 145, 0.25)"}`,
  marginBottom: 4,
});

const thankYouCheckIconStyle = () => ({
  fontSize: 52,
  color: "#009391",
});

const thankYouTitleStyle = (mode) => ({
  margin: 0,
  color: mode === "dark" ? "#e8fffc" : "#0a5c58",
  fontSize: 26,
  fontWeight: 700,
  letterSpacing: "-0.02em",
});

const thankYouLeadStyle = (mode) => ({
  margin: 0,
  color: mode === "dark" ? "#b8d9d6" : "#334155",
  fontSize: 15,
  lineHeight: 1.55,
  maxWidth: 360,
});

const thankYouFormNameStyle = (mode) => ({
  color: mode === "dark" ? "#8de0db" : "#0c6a65",
  fontWeight: 700,
});

const thankYouApiNoteStyle = (mode) => ({
  margin: 0,
  color: mode === "dark" ? "#86a8a5" : "#64748b",
  fontSize: 13,
  lineHeight: 1.5,
  maxWidth: 360,
});

export default CompanyFormFill;
